import React from 'react';
import { Task } from '../types';
import { User } from '../lib/auth';
import { GitMerge, GitPullRequest, CheckCircle2, Zap, FileText, PlusSquare } from 'lucide-react';

interface TaskGitHubStatusProps {
  task: Task;
  user: User | null;
  className?: string;
}

const TaskGitHubStatus: React.FC<TaskGitHubStatusProps> = ({ task, user, className }) => {
  if (!user) {
    return null;
  }

  const isCreator = user.internalUserId === task.user_id;

  let statusInfo: {
    icon: React.ReactNode;
    text: string;
    className: string;
    link?: string;
    linkText?: string;
  } | null = null;

  const githubUrl = task.github_repo ? `https://github.com/${task.github_repo}` : '';

  // Determine status based on the most advanced stage in the GitHub flow
  if (task.github_commit_sha && githubUrl) {
    statusInfo = {
      icon: <CheckCircle2 className="w-4 h-4" />,
      text: 'Completed',
      className: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300',
      link: `${githubUrl}/commit/${task.github_commit_sha}`,
      linkText: 'View Commit'
    };
  } else if (task.github_pr_id && githubUrl) {
    statusInfo = {
      icon: <GitPullRequest className="w-4 h-4" />,
      text: 'PR Open',
      className: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-300',
      link: `${githubUrl}/pull/${task.github_pr_id}`,
      linkText: `PR #${task.github_pr_id}`
    };
  } else if (task.github_issue_id && githubUrl) {
    statusInfo = {
      icon: <GitMerge className="w-4 h-4" />,
      text: 'Issue Created',
      className: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300',
      link: `${githubUrl}/issues/${task.github_issue_id}`,
      linkText: `Issue #${task.github_issue_id}`
    };
  } else if (task.selected_solution) {
    if (isCreator) {
        statusInfo = {
            icon: <PlusSquare className="w-4 h-4" />,
            text: 'Create GitHub Issue',
            className: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300',
        };
    } else {
        statusInfo = {
            icon: <CheckCircle2 className="w-4 h-4" />,
            text: 'Solution Applied',
            className: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300',
        };
    }
  } else if (task.has_ai_history) {
    if (isCreator) {
        statusInfo = {
            icon: <Zap className="w-4 h-4" />,
            text: 'Find a Solution',
            className: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300',
        };
    } else {
        statusInfo = {
            icon: <Zap className="w-4 h-4" />,
            text: 'AI Suggestions Available',
            className: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300',
        };
    }
  }

  if (!statusInfo) {
    return null;
  }

  return (
    <div className={`flex flex-col items-start gap-2 ${className}`}>
        <span className={`inline-flex items-center gap-2 px-2 py-1 rounded-full text-xs font-medium ${statusInfo.className}`}>
            {statusInfo.icon}
            <span className="font-medium">{statusInfo.text}</span>
        </span>
        {statusInfo.link && (
            <a
            href={statusInfo.link}
            target="_blank"
            rel="noopener noreferrer"
            className="text-xs text-muted-foreground hover:underline flex items-center gap-1"
            onClick={(e) => e.stopPropagation()} // Prevent card link from firing
            >
            <FileText className="w-3 h-3" />
            {statusInfo.linkText}
            </a>
        )}
    </div>
  );
};

export default TaskGitHubStatus;
